import Session from 'supertokens-auth-react/recipe/session';
import EmailPassword from 'supertokens-auth-react/recipe/emailpassword';
import { B2B_ROLE, AUTH_ERROR_CODE, AUTH_MESSAGES } from '@petseo/shared';

export interface LoginCredentials {
  email: string;
  password: string;
}

export interface RegisterCredentials {
  email: string;
  password: string;
}

export interface User {
  id: string;
  email: string;
}

class AuthService {
  private static readonly fieldErrorTranslations: Record<string, string> = {
    'This email already exists. Please sign in instead.': 'Ten adres email jest już zarejestrowany. Zaloguj się.',
    'Field is not optional': 'Pole jest wymagane',
  };

  private translateFieldError(error: string): string {
    return AuthService.fieldErrorTranslations[error] ?? error;
  }

  private handleAuthError(response: unknown): never {
    const responseStatus = (response as { status?: string }).status;

    if (responseStatus === 'FIELD_ERROR') {
      const fieldErrors = (response as { formFields: { id: string; error: string }[] }).formFields
        .map((field) => this.translateFieldError(field.error))
        .join(', ');
      throw new Error(fieldErrors);
    } else if (responseStatus === 'WRONG_CREDENTIALS_ERROR') {
      throw new Error('Nieprawidłowy email lub hasło');
    } else if (responseStatus === 'SIGN_IN_NOT_ALLOWED') {
      throw new Error('Brak dostępu do aplikacji...');
    } else if (responseStatus === 'SIGN_UP_NOT_ALLOWED') {
      throw new Error('Rejestracja nie jest dozwolona');
    }

    throw new Error('Operacja nie powiodła się');
  }

  async login(credentials: LoginCredentials) {
    try {
      const response = await EmailPassword.signIn({
        formFields: [
          { id: 'email', value: credentials.email },
          { id: 'password', value: credentials.password },
        ],
      });

      if (response.status === 'OK') {
        return {
          success: true,
          user: {
            id: response.user.id,
            email: response.user.emails[0],
          } as User,
        };
      }

      this.handleAuthError(response);
    } catch (error) {
      if (error instanceof Error && error.message === 'Failed to fetch') {
        throw new Error('Serwer niedostępny');
      }
      throw error;
    }
  }

  async register(credentials: RegisterCredentials) {
    try {
      const response = await EmailPassword.signUp({
        formFields: [
          { id: 'email', value: credentials.email },
          { id: 'password', value: credentials.password },
          { id: 'role', value: B2B_ROLE },
        ],
      });

      if (response.status === 'OK') {
        return {
          success: true,
          user: {
            id: response.user.id,
            email: response.user.emails[0],
          } as User,
        };
      }

      if (response.status === 'SIGN_UP_NOT_ALLOWED') {
        const reason = (response as { reason?: string }).reason;
        if (reason === AUTH_ERROR_CODE.ROLE_CONFIRMATION_SENT) {
          return { roleConfirmationSent: true as const };
        }
        if (reason === AUTH_ERROR_CODE.EMAIL_EXISTS_OTHER_METHOD) {
          throw new Error(AUTH_MESSAGES.EMAIL_EXISTS_OTHER_METHOD);
        }
      }

      this.handleAuthError(response);
    } catch (error) {
      if (error instanceof Error && error.message === 'Failed to fetch') {
        throw new Error('Serwer niedostępny');
      }
      throw error;
    }
  }

  async sendPasswordResetEmail(email: string) {
    try {
      const response = await EmailPassword.sendPasswordResetEmail({
        formFields: [{ id: 'email', value: email }],
      });

      if (response.status === 'OK' || response.status === 'PASSWORD_RESET_NOT_ALLOWED') {
        return { success: true };
      }

      this.handleAuthError(response);
    } catch (error) {
      if (error instanceof Error && error.message === 'Failed to fetch') {
        throw new Error('Serwer niedostępny');
      }
      throw error;
    }
  }

  async logout() {
    await Session.signOut();
  }

  async isAuthenticated(): Promise<boolean> {
    return await Session.doesSessionExist();
  }

  async getCurrentUser(): Promise<User | null> {
    if (await Session.doesSessionExist()) {
      const userId = await Session.getUserId();
      const userInfo: Record<string, unknown> = {};

      return {
        id: userId,
        email: (userInfo['email'] as string) || '',
      };
    }

    return null;
  }

  async getAccessToken(): Promise<string | null> {
    if (await Session.doesSessionExist()) {
      const token = await Session.getAccessToken();
      return token || null;
    }

    return null;
  }

  async isEmailVerified(): Promise<boolean> {
    try {
      const payload = await Session.getAccessTokenPayloadSecurely();
      if (payload && typeof payload['st-ev'] === 'object' && payload['st-ev'] !== null) {
        return (payload['st-ev'] as { v: boolean }).v === true;
      }
      return false;
    } catch {
      return false;
    }
  }

  async userAssociatedRoles(): Promise<string[]> {
    try {
      const payload = await Session.getAccessTokenPayloadSecurely();

      if (payload && typeof payload['st-role'] === 'object' && payload['st-role'] !== null && Array.isArray(payload['st-role'].v)) {
        return payload['st-role'].v;
      }

      return [];
    } catch (error) {
      console.error('[AUTH] Session check error:', error);
      return [];
    }
  }
}

export const authService = new AuthService();
